import React, { useMemo } from 'react';
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles';
import {
  APP_HEADER_HEIGHT_PX,
  getKeepAliveDialogMaxHeight,
  isTabKeepAliveEnabled,
  LAYOUT_CHROME_TOP_CSS_VAR,
  LAYOUT_DRAWER_WIDTH_CSS_VAR,
  TAB_BAR_HEIGHT_PX,
} from '../config/tabKeepAlive';

interface KeepAlivePaneThemeProviderProps {
  children: React.ReactNode;
}

/** Espaço ocupado pelo AppBar + barra de abas keep-alive. */
export const KEEP_ALIVE_CHROME_OFFSET_PX = APP_HEADER_HEIGHT_PX + TAB_BAR_HEIGHT_PX;

/**
 * Mantém Dialog/Modal/Popover no DOM do pane (sem portal no body) para ocultar ao trocar de aba.
 * O backdrop permanece habilitado para click-outside fechar filtros e dialogs.
 */
export const KeepAlivePaneThemeProvider: React.FC<KeepAlivePaneThemeProviderProps> = ({
  children,
}) => {
  const outerTheme = useTheme();

  const theme = useMemo(() => {
    if (!isTabKeepAliveEnabled()) {
      return outerTheme;
    }

    return createTheme(outerTheme, {
      components: {
        MuiBackdrop: {
          styleOverrides: {
            root: {
              // Evita re-fade do backdrop quando dialogs re-renderizam (ex.: polling do monitoramento).
              transition: 'none !important',
            },
          },
        },
        MuiModal: {
          defaultProps: {
            disablePortal: true,
            // Panes inativos continuam montados; um modal preservado não pode travar o scroll global.
            disableScrollLock: true,
          },
        },
        MuiDialog: {
          defaultProps: {
            disablePortal: true,
            slotProps: {
              backdrop: {
                transitionDuration: 0,
              },
            },
          },
          styleOverrides: {
            container: {
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: `var(${LAYOUT_CHROME_TOP_CSS_VAR}, ${KEEP_ALIVE_CHROME_OFFSET_PX}px)`,
              marginLeft: `var(${LAYOUT_DRAWER_WIDTH_CSS_VAR}, 0px)`,
              width: `calc(100% - var(${LAYOUT_DRAWER_WIDTH_CSS_VAR}, 0px))`,
              height: `calc(100% - var(${LAYOUT_CHROME_TOP_CSS_VAR}, ${KEEP_ALIVE_CHROME_OFFSET_PX}px))`,
            },
            paper: ({ ownerState }: { ownerState: { fullScreen?: boolean } }) => ({
              ...(ownerState.fullScreen
                ? {}
                : {
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: getKeepAliveDialogMaxHeight(false),
                    margin: 16,
                  }),
            }),
          },
        },
        MuiPopover: {
          defaultProps: {
            disablePortal: true,
          },
        },
      },
    });
  }, [outerTheme]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
